import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';
import {
  Send, Loader2, ArrowLeft, Sparkles, Mail, Clock, Users, Check,
  Search, Filter, X, Calendar, AlertTriangle, CheckCircle2, Trash2,
  ChevronDown, Eye
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTEURS = [
  { id: '', label: 'Tous les secteurs' },
  { id: 'entreprise', label: 'Entreprises' },
  { id: 'ce', label: 'CSE / CE' },
  { id: 'seminaire', label: 'Seminaires' },
  { id: 'mairie', label: 'Collectivites' },
  { id: 'association', label: 'Associations' },
  { id: 'transport', label: 'Transport' },
  { id: 'immobilier', label: 'Immobilier' },
  { id: 'services', label: 'Services' },
  { id: 'bienetre', label: 'Bien-etre' },
  { id: 'banque', label: 'Banque' },
  { id: 'evenement', label: 'Evenementiel' },
  { id: 'commerce', label: 'Commerce' },
  { id: 'ecole', label: 'Education' },
];

export default function CrmCampagne() {
  const [view, setView] = useState('list'); // list | create | detail
  const [audienceType, setAudienceType] = useState('prospects'); // prospects | clients
  const [includeNoOptin, setIncludeNoOptin] = useState(false);
  const [prospects, setProspects] = useState([]);
  const [clients, setClients] = useState([]);
  const [campagnes, setCampagnes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filterSecteur, setFilterSecteur] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [filterClientStatus, setFilterClientStatus] = useState(''); // Excellent / Very good / Normal
  const [filterMinVisites, setFilterMinVisites] = useState(0);
  const [searchQ, setSearchQ] = useState('');

  // Campaign form
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sendAt, setSendAt] = useState('');
  const [interval, setInterval] = useState(5);
  const [sender, setSender] = useState('restaurant');
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewProspect, setPreviewProspect] = useState(null);
  const [detailCampagne, setDetailCampagne] = useState(null);
  const [activeDetail, setActiveDetail] = useState(null);
  const [detailStatusFilter, setDetailStatusFilter] = useState('');
  const [detailOpenedOnly, setDetailOpenedOnly] = useState(false);

  const load = useCallback(async () => {
    try {
      const [pRes, cRes] = await Promise.all([
        fetch(`${API}/api/crm/prospects`),
        fetch(`${API}/api/crm/campagnes-email`),
      ]);
      const pData = await pRes.json();
      setProspects(Array.isArray(pData) ? pData : []);
      setCampagnes(await cRes.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load Zenchef clients (opt-in only OR all) — re-fetch when scope changes
  const loadClients = useCallback(async () => {
    setLoadingClients(true);
    try {
      const optInParam = includeNoOptin ? '' : '&opt_in=yes';
      const r = await fetch(`${API}/api/clients?source=zenchef${optInParam}&limit=5000`);
      const data = await r.json();
      setClients(Array.isArray(data) ? data : []);
    } catch {}
    setLoadingClients(false);
  }, [includeNoOptin]);

  useEffect(() => {
    if (audienceType === 'clients') loadClients();
  }, [audienceType, loadClients]);

  // Auto-refresh active campaign detail every 30s (must be unconditional)
  const activeCampaignId = campagnes.find(c => c.status === 'active')?.id;
  useEffect(() => {
    if (!activeCampaignId) { setActiveDetail(null); return; }
    let cancelled = false;
    const fetchDetail = async () => {
      try {
        const r = await fetch(`${API}/api/crm/campagne-email/${activeCampaignId}`);
        const d = await r.json();
        if (!cancelled) setActiveDetail(d);
      } catch {}
    };
    fetchDetail();
    const id = setInterval(fetchDetail, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [activeCampaignId]);

  // ═══ Audience selection (prospects B2B or Zenchef clients) ═══
  const items = audienceType === 'prospects' ? prospects : clients;

  const filtered = items.filter(p => {
    if (audienceType === 'prospects') {
      if (filterSecteur && p.secteur !== filterSecteur) return false;
      if (filterStatut && p.statut !== filterStatut) return false;
      if (searchQ) {
        const q = searchQ.toLowerCase();
        if (!((p.nom || '').toLowerCase().includes(q) || (p.contact || '').toLowerCase().includes(q) || (p.ville || '').toLowerCase().includes(q))) return false;
      }
    } else {
      // clients (Zenchef)
      if (filterClientStatus && (p.statut_client || '') !== filterClientStatus) return false;
      if (filterMinVisites > 0 && (p.nb_visites || 0) < filterMinVisites) return false;
      if (searchQ) {
        const q = searchQ.toLowerCase();
        const full = `${p.prenom || ''} ${p.nom || ''}`.toLowerCase();
        if (!(full.includes(q) || (p.email || '').toLowerCase().includes(q) || (p.ville || '').toLowerCase().includes(q))) return false;
      }
    }
    return true;
  });

  const withEmail = filtered.filter(p => p.email && p.email.includes('@'));
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const selectAll = () => {
    if (selectedIds.size === withEmail.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(withEmail.map(p => p.id)));
    }
  };

  const generateAI = async () => {
    setGenerating(true);
    try {
      const r = await fetch(`${API}/api/crm/campagne-email/generate-ia`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ secteur: filterSecteur || 'entreprise', objectif: 'premier contact', ton: 'professionnel' }),
      });
      const data = await r.json();
      if (data.subject) setSubject(data.subject);
      if (data.body) setBody(data.body);
      toast.success('Email genere par IA');
    } catch { toast.error('Erreur IA'); }
    setGenerating(false);
  };

  const sendCampagne = async () => {
    if (selectedIds.size === 0) { toast.error('Selectionnez des destinataires'); return; }
    if (!subject || !body) { toast.error('Sujet et message requis'); return; }
    setSending(true);
    try {
      const ids = Array.from(selectedIds);
      const payload = {
        subject, body, sender,
        send_at: sendAt || '',
        interval_minutes: interval,
        audience: audienceType,
        // B2C clients → no auto follow-up sequence J+5/J+12/J+20
        enable_sequence: audienceType === 'prospects',
      };
      if (audienceType === 'prospects') payload.prospect_ids = ids;
      else { payload.client_ids = ids; payload.include_no_optin = includeNoOptin; }

      const r = await fetch(`${API}/api/crm/campagne-email`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (data.campagne_id) {
        toast.success(`Campagne creee ! ${data.scheduled} email(s) programmes`);
        if (data.skipped?.length > 0) {
          toast.warning(`${data.skipped.length} destinataire(s) ignore(s)`);
        }
        setView('list');
        setSelectedIds(new Set());
        setSubject(''); setBody('');
        load();
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch { toast.error('Erreur connexion'); }
    setSending(false);
  };

  const openDetail = async (id) => {
    try {
      const r = await fetch(`${API}/api/crm/campagne-email/${id}`);
      setDetailCampagne(await r.json());
      setView('detail');
    } catch { toast.error('Erreur'); }
  };

  const cancelCampagne = async (id) => {
    if (!window.confirm('Annuler cette campagne ?')) return;
    try {
      const r = await fetch(`${API}/api/crm/campagne-email/${id}`, { method: 'DELETE' });
      const data = await r.json();
      toast.success(`${data.cancelled} email(s) annule(s)`);
      load();
      setView('list');
    } catch {}
  };

  const previewBody = (p) => {
    const prenom = p.prenom || (p.contact || '').split(' ')[0] || '';
    return body
      .replace(/{prenom}/g, prenom)
      .replace(/{nom}/g, p.nom || '')
      .replace(/{contact}/g, p.contact || prenom)
      .replace(/{ville}/g, p.ville || '')
      .replace(/{secteur}/g, p.secteur || '');
  };

  // ═══ DETAIL VIEW ═══
  if (view === 'detail' && detailCampagne) {
    const c = detailCampagne;
    const allEmails = c.emails || [];
    const filteredEmails = allEmails.filter(e => {
      if (detailStatusFilter && e.status !== detailStatusFilter) return false;
      if (detailOpenedOnly && !(e.open_count > 0)) return false;
      return true;
    });
    return (
      <div className="space-y-4" data-testid="campagne-detail">
        <button onClick={() => setView('list')} className="flex items-center gap-1 text-xs text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]">
          <ArrowLeft size={14} /> Retour
        </button>
        <div className="bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/30 rounded-xl p-4">
          <h3 className="text-sm font-bold text-[rgb(var(--lm-text))] mb-1">{c.subject}</h3>
          <div className="flex gap-4 text-[9px] text-[rgb(var(--lm-text-muted))]">
            <span>{c.total_scheduled} envoye(s)</span>
            <span>{c.total_skipped} ignore(s)</span>
            <span>{new Date(c.created_at).toLocaleDateString('fr-FR')}</span>
          </div>
          {c.stats && (
            <>
              <div className="flex gap-2 mt-3 flex-wrap">
                {Object.entries(c.stats).filter(([k]) => !['opened', 'open_rate'].includes(k)).map(([k, v]) => v > 0 && (
                  <span key={k} className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${k === 'envoye' ? 'bg-green-100 text-green-700' : k === 'erreur' ? 'bg-red-100 text-red-700' : k === 'annule' ? 'bg-gray-100 text-gray-600' : 'bg-blue-100 text-blue-700'}`}>
                    {k}: {v}
                  </span>
                ))}
                {c.stats.envoye > 0 && (
                  <span className="text-[9px] px-2 py-0.5 rounded-full font-bold bg-purple-100 text-purple-700" data-testid="campaign-open-rate">
                    👁️ {c.stats.opened || 0} ouvert(s) · {c.stats.open_rate || 0}%
                  </span>
                )}
              </div>
              {/* Filter bar */}
              <div className="flex gap-1.5 mt-3 flex-wrap">
                <button onClick={() => { setDetailStatusFilter(''); setDetailOpenedOnly(false); }}
                  className={`text-[9px] px-2 py-1 rounded-full font-bold ${!detailStatusFilter && !detailOpenedOnly ? 'bg-[rgb(var(--lm-gold))] text-white' : 'bg-gray-100 text-gray-600'}`}>
                  Tous ({allEmails.length})
                </button>
                <button onClick={() => { setDetailStatusFilter('envoye'); setDetailOpenedOnly(false); }}
                  className={`text-[9px] px-2 py-1 rounded-full font-bold ${detailStatusFilter === 'envoye' && !detailOpenedOnly ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700'}`}>
                  ✓ Envoyés
                </button>
                <button onClick={() => { setDetailStatusFilter('envoye'); setDetailOpenedOnly(true); }}
                  className={`text-[9px] px-2 py-1 rounded-full font-bold ${detailOpenedOnly ? 'bg-purple-600 text-white' : 'bg-purple-50 text-purple-700'}`}
                  data-testid="filter-opened-only">
                  👁️ Ouverts ({c.stats.opened || 0})
                </button>
                <button onClick={() => { setDetailStatusFilter('programme'); setDetailOpenedOnly(false); }}
                  className={`text-[9px] px-2 py-1 rounded-full font-bold ${detailStatusFilter === 'programme' ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-700'}`}>
                  ⏳ En attente
                </button>
                <button onClick={() => { setDetailStatusFilter('erreur'); setDetailOpenedOnly(false); }}
                  className={`text-[9px] px-2 py-1 rounded-full font-bold ${detailStatusFilter === 'erreur' ? 'bg-red-600 text-white' : 'bg-red-50 text-red-700'}`}>
                  ✗ Erreurs
                </button>
              </div>
            </>
          )}
          {c.status === 'active' && (
            <Button onClick={() => cancelCampagne(c.id)} size="sm" variant="outline" className="mt-3 text-xs h-7 text-red-500 border-red-200">
              <X size={12} className="mr-1" /> Annuler la campagne
            </Button>
          )}
        </div>
        <div className="space-y-1.5">
          {filteredEmails.map((e, i) => (
            <div key={i} className="bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/20 rounded-lg px-3 py-2 flex items-center gap-3">
              <div className={`w-2 h-2 rounded-full ${e.status === 'envoye' ? 'bg-green-500' : e.status === 'erreur' ? 'bg-red-500' : e.status === 'annule' ? 'bg-gray-400' : 'bg-blue-500'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold text-[rgb(var(--lm-text))] truncate flex items-center gap-1.5">
                  {e.prospect_nom}
                  {e.is_followup && <span className="px-1 py-0.5 bg-amber-100 text-amber-700 rounded text-[7px] font-bold">J+{e.sequence_step === 1 ? 5 : e.sequence_step === 2 ? 12 : 20}</span>}
                  {e.open_count > 0 && (
                    <span className="px-1 py-0.5 bg-purple-100 text-purple-700 rounded text-[7px] font-bold flex items-center gap-0.5">
                      👁️ {e.open_count > 1 ? `${e.open_count}×` : 'lu'}
                    </span>
                  )}
                </p>
                <p className="text-[9px] text-[rgb(var(--lm-text-muted))] truncate">{e.prospect_email}</p>
                {e.last_opened_at && (
                  <p className="text-[8px] text-purple-600 font-semibold">Dernière ouverture : {new Date(e.last_opened_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</p>
                )}
              </div>
              <span className="text-[8px] text-[rgb(var(--lm-text-muted))]">
                {new Date(e.send_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          ))}
          {filteredEmails.length === 0 && (
            <div className="text-center py-8 text-xs text-[rgb(var(--lm-text-muted))]">
              Aucun email avec ce filtre.
            </div>
          )}
        </div>
      </div>
    );
  }

  // ═══ CREATE VIEW ═══
  if (view === 'create') {
    return (
      <div className="space-y-4" data-testid="campagne-create">
        <div className="flex items-center justify-between">
          <button onClick={() => setView('list')} className="flex items-center gap-1 text-xs text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]">
            <ArrowLeft size={14} /> Retour
          </button>
          <span className="text-xs text-[rgb(var(--lm-gold))] font-bold">{selectedIds.size} destinataire(s)</span>
        </div>

        {/* Audience switcher — Prospects B2B vs Clients Zenchef */}
        <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))]/30" data-testid="audience-switch">
          <button
            onClick={() => { setAudienceType('prospects'); setSelectedIds(new Set()); setSearchQ(''); }}
            className={`py-2 rounded-lg text-[11px] font-bold transition-all ${audienceType === 'prospects' ? 'bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-navy))]' : 'text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]'}`}
            data-testid="audience-prospects">
            Prospects B2B ({prospects.length})
          </button>
          <button
            onClick={() => { setAudienceType('clients'); setSelectedIds(new Set()); setSearchQ(''); }}
            className={`py-2 rounded-lg text-[11px] font-bold transition-all ${audienceType === 'clients' ? 'bg-rose-500 text-white' : 'text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-text))]'}`}
            data-testid="audience-clients">
            Clients Zenchef ({clients.length || '...'})
          </button>
        </div>

        {audienceType === 'clients' && (
          <div className={`border rounded-lg px-3 py-2 text-[10px] leading-relaxed ${includeNoOptin ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-rose-50 border-rose-200 text-rose-700'}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                {includeNoOptin ? (
                  <><strong>⚠️ TOUS les contacts (3316)</strong> · Inclut les clients sans opt-in marketing. Risque RGPD : à n'utiliser que si vous avez un motif légitime (relation contractuelle, communication transactionnelle).</>
                ) : (
                  <><strong>Mode B2C — Fidélisation (475 opt-in)</strong> · Seuls les clients ayant donné leur opt-in email Zenchef sont affichés (RGPD). Pas de relances J+5/J+12/J+20.</>
                )}
              </div>
              <button
                onClick={() => { setIncludeNoOptin(v => !v); setSelectedIds(new Set()); }}
                className={`shrink-0 text-[10px] font-bold px-2.5 py-1 rounded-full border transition-all ${includeNoOptin ? 'bg-rose-500 text-white border-rose-500 hover:bg-rose-600' : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-100'}`}
                data-testid="toggle-include-no-optin">
                {includeNoOptin ? '← Revenir aux opt-in (475)' : 'Inclure tous (3316) →'}
              </button>
            </div>
          </div>
        )}

        {/* Filters + Selection */}
        <div className="bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/30 rounded-xl p-3 space-y-2">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[rgb(var(--lm-text-muted))]" />
              <Input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Rechercher..."
                className="text-xs h-8 pl-7 bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))]" data-testid="campagne-search" />
            </div>
            {audienceType === 'prospects' ? (
              <select value={filterSecteur} onChange={e => setFilterSecteur(e.target.value)}
                className="text-[10px] bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] rounded-lg px-2 h-8 text-[rgb(var(--lm-text))]" data-testid="campagne-filter-secteur">
                {SECTEURS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            ) : (
              <>
                <select value={filterClientStatus} onChange={e => setFilterClientStatus(e.target.value)}
                  className="text-[10px] bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] rounded-lg px-2 h-8 text-[rgb(var(--lm-text))]" data-testid="campagne-filter-clientstatus">
                  <option value="">Tous statuts</option>
                  <option value="Excellent customer">⭐ Excellent</option>
                  <option value="Very good customer">🔵 Very good</option>
                  <option value="Normal">Normal</option>
                </select>
                <select value={filterMinVisites} onChange={e => setFilterMinVisites(Number(e.target.value))}
                  className="text-[10px] bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] rounded-lg px-2 h-8 text-[rgb(var(--lm-text))]" data-testid="campagne-filter-visites">
                  <option value={0}>Toutes fréquences</option>
                  <option value={10}>VIP (10+ visites)</option>
                  <option value={4}>Habitués (4+)</option>
                  <option value={2}>Réguliers (2+)</option>
                  <option value={1}>Au moins 1 visite</option>
                </select>
              </>
            )}
          </div>
          <div className="flex items-center justify-between">
            <button onClick={selectAll} className="text-[10px] text-[rgb(var(--lm-gold))] hover:underline" data-testid="campagne-select-all">
              {selectedIds.size === withEmail.length && withEmail.length > 0 ? 'Tout deselectionner' : `Tout selectionner (${withEmail.length})`}
            </button>
            <span className="text-[9px] text-[rgb(var(--lm-text-muted))]">{withEmail.length} avec email / {filtered.length} total</span>
          </div>
          <div className="max-h-48 overflow-y-auto space-y-0.5">
            {loadingClients && audienceType === 'clients' && (
              <div className="flex justify-center py-4"><Loader2 size={14} className="animate-spin text-[rgb(var(--lm-text-muted))]" /></div>
            )}
            {withEmail.map(p => {
              const displayName = audienceType === 'clients'
                ? `${p.prenom || ''} ${p.nom || ''}`.trim() || p.email
                : p.nom;
              const sub = audienceType === 'clients'
                ? `${p.statut_client || ''}${p.nb_visites ? ` · ${p.nb_visites} visite${p.nb_visites > 1 ? 's' : ''}` : ''}`
                : (p.secteur || '');
              return (
                <label key={p.id} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-[rgb(var(--lm-bg))] cursor-pointer" data-testid={`prospect-${p.id}`}>
                  <input type="checkbox" checked={selectedIds.has(p.id)} onChange={() => toggleSelect(p.id)}
                    className="w-3.5 h-3.5 rounded border-gray-300" />
                  <span className="text-[10px] text-[rgb(var(--lm-text))] flex-1 truncate">{displayName}</span>
                  <span className="text-[8px] text-[rgb(var(--lm-text-muted))]">{sub}</span>
                  <span className="text-[8px] text-[rgb(var(--lm-text-muted))] truncate max-w-[120px]">{p.email}</span>
                </label>
              );
            })}
          </div>
        </div>

        {/* Email composition */}
        <div className="bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/30 rounded-xl p-3 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-[11px] font-bold text-[rgb(var(--lm-text))] uppercase tracking-wider">Composer l'email</h3>
            <Button onClick={generateAI} disabled={generating} size="sm" variant="outline" className="text-[10px] h-7 border-[rgb(var(--lm-border))]" data-testid="campagne-generate-ia">
              {generating ? <Loader2 size={11} className="animate-spin mr-1" /> : <Sparkles size={11} className="mr-1" />}
              Generer IA
            </Button>
          </div>

          <div className="flex gap-2 text-[9px] text-[rgb(var(--lm-text-muted))] bg-[rgb(var(--lm-bg))] rounded-lg px-2.5 py-1.5">
            <span className="font-mono">{'{prenom}'}</span>
            <span className="font-mono">{'{contact}'}</span>
            <span className="font-mono">{'{nom}'}</span>
            <span className="font-mono">{'{ville}'}</span>
            <span className="opacity-50">= variables auto</span>
          </div>

          <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Objet de l'email"
            className="text-xs bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] h-9" data-testid="campagne-subject" />

          <textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Corps de l'email...&#10;&#10;Utilisez {contact}, {nom}, {ville} pour personnaliser"
            rows={8} className="w-full text-xs bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] rounded-lg px-3 py-2 text-[rgb(var(--lm-text))] placeholder:text-[rgb(var(--lm-text-muted))]/40 focus:outline-none resize-none"
            data-testid="campagne-body" />

          {/* Preview */}
          {body && withEmail.length > 0 && (
            <div className="bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))]/20 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-2">
                <Eye size={11} className="text-[rgb(var(--lm-text-muted))]" />
                <span className="text-[9px] font-bold text-[rgb(var(--lm-text-muted))] uppercase tracking-wider">
                  Apercu pour {audienceType === 'clients'
                    ? `${withEmail[0]?.prenom || ''} ${withEmail[0]?.nom || ''}`.trim()
                    : withEmail[0]?.nom}
                </span>
              </div>
              <p className="text-[10px] font-bold text-[rgb(var(--lm-text))] mb-1">
                {subject
                  .replace(/{prenom}/g, withEmail[0]?.prenom || (withEmail[0]?.contact || '').split(' ')[0] || '')
                  .replace(/{nom}/g, withEmail[0]?.nom || '')
                  .replace(/{contact}/g, withEmail[0]?.contact || withEmail[0]?.prenom || '')}
              </p>
              <p className="text-[10px] text-[rgb(var(--lm-text-secondary))] whitespace-pre-wrap leading-relaxed">{previewBody(withEmail[0])}</p>
            </div>
          )}

          {/* Schedule */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[9px] text-[rgb(var(--lm-text-muted))] block mb-1">Date/heure d'envoi</label>
              <Input type="datetime-local" value={sendAt} onChange={e => setSendAt(e.target.value)}
                className="text-[10px] bg-[rgb(var(--lm-bg))] border-[rgb(var(--lm-border))] h-8" data-testid="campagne-sendat" />
              <p className="text-[8px] text-[rgb(var(--lm-text-muted))] mt-0.5">Vide = dans 10 min</p>
            </div>
            <div>
              <label className="text-[9px] text-[rgb(var(--lm-text-muted))] block mb-1">Intervalle entre emails</label>
              <select value={interval} onChange={e => setInterval(Number(e.target.value))}
                className="w-full text-[10px] bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] rounded-lg px-2 h-8 text-[rgb(var(--lm-text))]" data-testid="campagne-interval">
                <option value={2}>2 min</option>
                <option value={5}>5 min</option>
                <option value={10}>10 min</option>
                <option value={15}>15 min</option>
                <option value={30}>30 min</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-[9px] text-[rgb(var(--lm-text-muted))] block mb-1">Envoyer depuis</label>
            <select value={sender} onChange={e => setSender(e.target.value)}
              className="w-full text-[10px] bg-[rgb(var(--lm-bg))] border border-[rgb(var(--lm-border))] rounded-lg px-2 h-8 text-[rgb(var(--lm-text))]" data-testid="campagne-sender">
              <option value="restaurant">Restaurant (contact@restaurant-le-mas.fr)</option>
              <option value="traiteur">Traiteur (contact@traiteur-du-mas.fr)</option>
            </select>
          </div>
        </div>

        {/* Send */}
        <Button onClick={sendCampagne} disabled={sending || selectedIds.size === 0 || !subject || !body}
          className="w-full bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-navy))] text-xs h-10" data-testid="campagne-send">
          {sending ? <Loader2 size={14} className="animate-spin mr-2" /> : <Send size={14} className="mr-2" />}
          Programmer {selectedIds.size} email(s)
        </Button>
      </div>
    );
  }

  // ═══ LIST VIEW — Dashboard Prospection ═══
  // Compute global stats (hooks above — unconditional)
  const activeCampaign = campagnes.find(c => c.status === 'active');

  const stats = activeDetail?.stats || {};
  const sent = stats.envoye || 0;
  const programmed = stats.programme || 0;
  const errored = stats.erreur || 0;
  const activeTotal = sent + programmed + errored + (stats.annule || 0);
  const pct = activeTotal > 0 ? Math.round((sent / activeTotal) * 100) : 0;

  // Next scheduled emails from active campaign
  const nextEmails = (activeDetail?.emails || [])
    .filter(e => e.status === 'programme')
    .sort((a, b) => (a.send_at || '').localeCompare(b.send_at || ''))
    .slice(0, 5);

  const historyCampagnes = campagnes.filter(c => c.status !== 'active').slice(0, 10);

  return (
    <div className="space-y-4" data-testid="campagne-list">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[rgb(var(--lm-text))]">Prospection B2B</h2>
          <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">Campagnes email · Programmation · Suivi</p>
        </div>
        <Button onClick={() => setView('create')} size="sm" className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-navy))] text-xs h-8 font-bold" data-testid="campagne-new">
          <Send size={12} className="mr-1" /> Nouvelle campagne
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-[rgb(var(--lm-text-muted))]" /></div>
      ) : (
        <>
          {/* ACTIVE CAMPAIGN CARD — HERO */}
          {activeCampaign && activeDetail && (
            <div className="relative bg-gradient-to-br from-purple-50 via-white to-purple-50 border-2 border-purple-200 rounded-2xl p-4 overflow-hidden" data-testid="campaign-active-card">
              <div className="absolute top-2 right-2">
                <span className="text-[9px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-bold flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> EN COURS
                </span>
              </div>
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center shrink-0">
                  <Send size={18} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-900 truncate">{activeCampaign.subject}</p>
                  <p className="text-[10px] text-gray-500">Lancée le {new Date(activeCampaign.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
              </div>

              {/* Progress */}
              <div className="mb-3">
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="font-bold text-gray-900">{sent} / {activeTotal} envoyés</span>
                  <span className="text-purple-600 font-bold">{pct}%</span>
                </div>
                <div className="h-2.5 bg-purple-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-purple-500 to-purple-700 transition-all duration-500" style={{ width: `${pct}%` }} />
                </div>
              </div>

              {/* Stats pills */}
              <div className="flex gap-1.5 flex-wrap mb-3">
                {sent > 0 && <span className="px-2 py-1 rounded-full bg-green-100 text-green-700 text-[10px] font-bold">✓ {sent} envoyé{sent > 1 ? 's' : ''}</span>}
                {programmed > 0 && <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold">⏳ {programmed} programmé{programmed > 1 ? 's' : ''}</span>}
                {errored > 0 && <span className="px-2 py-1 rounded-full bg-red-100 text-red-700 text-[10px] font-bold">✗ {errored} erreur{errored > 1 ? 's' : ''}</span>}
              </div>

              {/* Next scheduled emails */}
              {nextEmails.length > 0 && (
                <div className="bg-white/70 rounded-xl p-3 border border-purple-100">
                  <p className="text-[10px] font-bold text-gray-700 mb-2 flex items-center gap-1">
                    <Clock size={10} className="text-purple-600" /> PROCHAINS ENVOIS
                  </p>
                  <div className="space-y-1">
                    {nextEmails.map((e, i) => (
                      <div key={i} className="flex items-center gap-2 text-[10px]">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                        <span className="text-gray-700 truncate flex-1">{e.prospect_nom}</span>
                        <span className="text-gray-500 font-mono">
                          {new Date(e.send_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button onClick={() => openDetail(activeCampaign.id)}
                className="mt-3 w-full py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold transition-colors flex items-center justify-center gap-1"
                data-testid="campaign-active-detail">
                <Eye size={12} /> Voir le détail complet
              </button>
            </div>
          )}

          {/* EMPTY STATE */}
          {!activeCampaign && campagnes.length === 0 && (
            <div className="text-center py-12 bg-[rgb(var(--lm-card))] rounded-xl border border-[rgb(var(--lm-border))]/30">
              <Mail size={32} className="text-[rgb(var(--lm-text-muted))]/20 mx-auto mb-3" />
              <p className="text-sm text-[rgb(var(--lm-text-muted))]">Aucune campagne</p>
              <p className="text-[10px] text-[rgb(var(--lm-text-muted))] mt-1">Envoyez des emails en masse a vos prospects</p>
              <Button onClick={() => setView('create')} size="sm" className="mt-4 bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-navy))] text-xs h-8">
                <Send size={12} className="mr-1" /> Créer une campagne
              </Button>
            </div>
          )}

          {/* HISTORY */}
          {historyCampagnes.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-wider text-[rgb(var(--lm-text-muted))] font-bold mb-2 mt-4">Historique</p>
              <div className="space-y-1.5">
                {historyCampagnes.map(c => (
                  <div key={c.id} onClick={() => openDetail(c.id)}
                    className="bg-[rgb(var(--lm-card))] border border-[rgb(var(--lm-border))]/30 rounded-lg p-3 hover:shadow-sm cursor-pointer transition-shadow"
                    data-testid={`campagne-${c.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-bold text-[rgb(var(--lm-text))] truncate flex-1">{c.subject}</p>
                      <span className={`text-[8px] px-1.5 py-0.5 rounded-full font-bold ml-2 shrink-0 ${c.status === 'terminee' ? 'bg-gray-100 text-gray-600' : 'bg-red-50 text-red-600'}`}>
                        {c.status}
                      </span>
                    </div>
                    <div className="flex gap-3 text-[9px] text-[rgb(var(--lm-text-muted))]">
                      <span className="flex items-center gap-0.5"><Users size={9} /> {c.total_scheduled} email(s)</span>
                      <span className="flex items-center gap-0.5"><Clock size={9} /> {new Date(c.send_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit' })}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
